/**
 * Authored GLB ship hulls (v49) — the hangar tier of the collection system.
 *
 * A `hull` part in a ShipLayout replaces the whole part-assembled visual with an
 * authored mesh; everything downstream still speaks BuiltShip, so the flight sim,
 * remotes, trails and guns cannot tell the difference. The glbVehicle.ts pattern,
 * one deck up: one in-flight load per URL, cloned per view, and a coarse painted
 * STAND-IN in the group immediately so the synchronous consumers (shipThumb
 * renders one frame; the shipyard opens on whatever is there) never see empty.
 *
 * Landmarks here are ship-local (-Z forward, +Y up) measured off the authored
 * model: the builder prints them on every run (`npm run build:lightspeed`), and
 * model→ship is `ship_x = -model_z, ship_z = model_x` under the -π/2 yaw.
 */
import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import type { ShipMaterials, ThrusterTip, WeaponMount } from "./parts";

export interface GlbHullDef {
  url: string;
  /** Uniform metres-per-model-unit (hulls are authored at game scale: 1). */
  scale: number;
  /** Yaw mapping the model's -X nose onto the ship's -Z forward. */
  yaw: number;
  /** Exhaust points, ship-local — the trail system's emitters. */
  thrusterTips: readonly (readonly [number, number, number])[];
  /** Muzzles, ship-local, firing -Z. */
  weaponMounts: readonly { p: readonly [number, number, number]; g: 1 | 2 }[];
  /** Rough stand-in box (length, width, height) shown until the GLB lands. */
  standIn: readonly [number, number, number];
}

export const GLB_HULLS: Readonly<Record<string, GlbHullDef>> = {
  lightspeed: {
    url: "/models/ships/lightspeed.glb",
    scale: 1,
    yaw: -Math.PI / 2,
    // Model engine exits (4.75, -0.10, ±1.02) → ship (∓1.02, -0.10, 4.75).
    thrusterTips: [[-1.02, -0.1, 4.75], [1.02, -0.1, 4.75]],
    // Mid-wing hardpoints, model (1.0, -0.18, ±1.9) → ship (∓1.9, -0.18, 1.0).
    weaponMounts: [{ p: [-1.9, -0.18, 1.0], g: 1 }, { p: [1.9, -0.18, 1.0], g: 1 }],
    standIn: [9.35, 6.1, 2.5],
  },
};

const loads = new Map<string, Promise<THREE.Object3D>>();

function loadHull(url: string): Promise<THREE.Object3D> {
  let pending = loads.get(url);
  if (!pending) {
    pending = new GLTFLoader().loadAsync(url).then((gltf) => {
      gltf.scene.traverse((node) => {
        if (!(node instanceof THREE.Mesh)) return;
        // Emissive strips (Basic material) neither cast nor catch sun shadows,
        // matching buildShipFromLayout's rule for part-built ships.
        if (!(node.material instanceof THREE.MeshBasicMaterial)) {
          node.castShadow = true;
          node.receiveShadow = true;
        }
      });
      return gltf.scene;
    });
    loads.set(url, pending);
  }
  return pending;
}

/**
 * The hull part's visual group: a stand-in silhouette now, the authored mesh
 * when it streams in. Returns null for an unknown variant so buildPart can fall
 * through to its normal placeholder.
 */
export function buildHullPart(variant: string, mats: ShipMaterials): THREE.Group | null {
  const def = GLB_HULLS[variant];
  if (!def) return null;
  const part = new THREE.Group();
  part.name = `hull:${variant}`;

  const [len, wid, hei] = def.standIn;
  const standIn = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(wid * 0.34, hei * 0.7, len), mats.dark);
  const wings = new THREE.Mesh(new THREE.BoxGeometry(wid, hei * 0.12, len * 0.42), mats.hull);
  wings.position.set(0, -hei * 0.18, len * 0.18);
  standIn.add(body, wings);
  part.add(standIn);

  void loadHull(def.url).then((scene) => {
    // The part may have been disposed with its ship before the download landed —
    // adding to a detached group is harmless, so no ceremony needed.
    const model = scene.clone(true);
    model.scale.setScalar(def.scale);
    model.rotation.y = def.yaw;
    part.remove(standIn);
    part.add(model);
  }).catch((err: unknown) => {
    console.warn(`[ship] hull load failed: ${def.url}`, err);
  });
  return part;
}

/** The def's landmark tables as BuiltShip arrays (hull parts sit at the ship
 *  origin, so no per-instance transform is applied). */
export function hullTips(variant: string): ThrusterTip[] {
  const def = GLB_HULLS[variant];
  if (!def) return [];
  return def.thrusterTips.map((p) => ({
    offset: new THREE.Vector3(...p),
    color: new THREE.Color(0x4fa8ff),
  }));
}

export function hullMounts(variant: string): WeaponMount[] {
  const def = GLB_HULLS[variant];
  if (!def) return [];
  return def.weaponMounts.map((mnt) => ({
    offset: new THREE.Vector3(...mnt.p),
    dir: new THREE.Vector3(0, 0, -1),
    group: mnt.g,
  }));
}
